import * as React from 'react';
import { useEffect } from 'react';
import { Activity, PublicAmorgUser, rng, stringUtil, UserToken } from "../../../amorg-common";
import { createAgendaItem, getActivity } from '../../../api/activityClientApi';
import { getAuthorizedActiveUserOrRedirect } from '../../../api/serverApiHelpers';
import { getUsers } from '../../../api/userClientApi';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { SingleColumnLayout } from '../../_layouts/SingleColumnLayout';
import { ActivityComponentProps } from './ActivityComponentProps';
import { ActivityHeader } from './ActivityHeader';
import { AgendaItemListView } from './AgendaItemListView';
import { MeetingDetailsPanel } from './MeetingDetailsPanel';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { Button } from '../../../designSystem/button/Button';
import { AgendaItemMenuView } from './AgendaItemMenuView';
import { amorgLocalStorage } from '../../../utils/amorgLocalStorage';
import { PageSection } from '../../../designSystem/PageSection';

type MeetingActivityPageViewProps = {
    activityId: string,
    onActivityTitleChanged: (title: string) => void,
};

type State =
    { state: 'initial-loading'; } |
    { state: 'ready', activity: Activity, activeUser: UserToken, users: PublicAmorgUser[], loading: boolean; } |
    { state: 'error', errorMessage: string; };

export default function MeetingActivityPageView (props: MeetingActivityPageViewProps): JSX.Element 
{
    const [state, setState] = React.useState<State>({ state: 'initial-loading' });

    useEffect(() =>
    {
        updateState();
    }, [props.activityId]);

    async function updateState ()
    {
        if (state.state === 'ready')
        {
            setState({ ...state, loading: true });
        }

        const activity = await getActivity(props.activityId);
        if (!activity)
        {
            setState({ state: 'error', errorMessage: 'Ugyldig url' });
            return;
        }

        if (activity.activityType !== 'WorkgroupMeeting' && activity.activityType !== 'AnnualWorkgroupStatus')
        {
            setState({ state: 'error', errorMessage: 'Ugyldig aktivitetstype' });
            return;
        }

        props.onActivityTitleChanged(activity.title);

        const activeUser = await getAuthorizedActiveUserOrRedirect(['EditSurveys']);

        setState({
            state: 'ready',
            activity,
            activeUser,
            users: await getUsers(),
            loading: false
        });
    }

    let componentProps: ActivityComponentProps | null = null;
    if (state.state === 'ready')
    {
        componentProps = {
            activeUser: state.activeUser,
            activity: state.activity as Activity,
            users: state.users,
            onActivityUpdated: updateState
        };
    }

    return (
        <>
            <LoadingPageOverlay loading={state.state === 'initial-loading' || (state.state === 'ready' && state.loading)} />

            {state.state === 'error' && <p>{state.errorMessage}</p>}

            {state.state === 'ready' &&
                <AgendaMeetingActivityView {...(componentProps as ActivityComponentProps)} />
            }
        </>
    );
}

function AgendaMeetingActivityView (props: ActivityComponentProps & { displayMode?: 'list' | 'menu'; }): JSX.Element 
{
    const [viewMode, setViewMode] = React.useState<'list' | 'menu'>(props.displayMode ?? 'menu');
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() =>
    {
        setViewMode(amorgLocalStorage.meetingView.get(props.activity.id));
    }, [props.activity]);

    function handleViewModeChange (newViewMode: 'list' | 'menu')
    {
        setViewMode(newViewMode);
        amorgLocalStorage.meetingView.set(props.activity.id, newViewMode);
    }

    async function handleActivityUpdated ()
    {
        setLoading(true);
        props.onActivityUpdated();
        // setInvitationStatus(await getMeetingInvitationStatus(props.activity.id));
        setLoading(false);
    }

    async function handleClickCreateAgendaItem ()
    {
        const nameBase = 'Nyt agendapunkt';
        const nextTitle = stringUtil.generateNextSequenceValue(nameBase, props.activity.agendaItems.map(ap => ap.title));
        const id = rng.getRandomId();

        setLoading(true);
        const result = await createAgendaItem(id, props.activity.id, nextTitle, '', '');
        if (result.isSuccess)
        {
            props.onActivityUpdated();
        }
        setLoading(false);
    }

    return (
        <>

            <ActivityHeader
                activity={props.activity}
                deleteConfirmationTextInModal="Tilhørende handlepunkter vil ligeledes blive slettet."
                onActivityUpdated={handleActivityUpdated}
                isLoading={setLoading} />

            <SingleColumnLayout>

                <MeetingDetailsPanel
                    activity={props.activity}
                    descriptionText="Mødebeskrivelse"
                    periodSelection='date-time'
                    onActivityUpdated={handleActivityUpdated}
                    componentTitle='Møde info'
                    users={props.users}
                    participantTexts={{ header: 'Deltagere' }}
                    infoComponents={
                        <>
                            <LoadingPageOverlay loading={loading} />
                        </>
                    }
                />

                <PageSection
                    headerStyleVariant='minimal'
                    contentStyleVariant='minimal'
                    header={{
                        title:'Agenda',
                        defaultCollapsed:false,
                        customButtonContent:
                            <Flex justifyContent="flex-end" gap={4}>

                                {viewMode === 'list' &&
                                    <Button 
                                        icon="actions_plus"
                                        variant="filled"
                                        size='32px'
                                        text="Opret agendapunkt"
                                        emphasize={true}
                                        onClick={handleClickCreateAgendaItem}
                                    />
                                }

                                <Button variant={viewMode === 'menu' ? 'filled' : 'outlined'} size="32px" icon="view_column_2" 
                                    tooltipContent="Skift til menu visning"
                                    onClick={() => handleViewModeChange('menu')} />

                                <Button variant={viewMode === 'list' ? 'filled' : 'outlined'} size="32px" icon="navigation_menu" 
                                    tooltipContent="Skift til liste visning"
                                    onClick={() => handleViewModeChange('list')} />

                            </Flex>
                    }}>

                    <Flex flexDirection="column" gap={4}>
                        {viewMode === 'menu' &&
                            <AgendaItemMenuView {...props} onClickCreateAgendaItem={handleClickCreateAgendaItem} />
                        }

                        {viewMode === 'list' &&
                            <AgendaItemListView {...props} onClickCreateAgendaItem={handleClickCreateAgendaItem} />
                        }
                    </Flex>
                </PageSection>

            </SingleColumnLayout>
        </>
    );
}